import { authAxios } from './crmApi';

// ── TypeScript Interfaces ──

export interface Company {
  id: number;
  nip: string;
  krs: string | null;
  regon: string | null;
  name: string;
  short_name: string | null;
  address: {
    street?: string;
    city?: string;
    zip?: string;
    voivodeship?: string;
  };
  pkd_codes: string[];
  size: 'mikro' | 'mała' | 'średnia' | 'duża' | null;
  de_minimis_used: number;
  de_minimis_currency: string;
  source: string;
  ceidg_status?: 'aktywny' | 'zawieszony' | 'wykreślony' | null;
  notes: string | null;
  is_active: boolean;
  // New fields
  industry: string | null;
  company_status: 'active' | 'inactive' | 'prospect' | 'partner' | 'supplier' | null;
  annual_revenue: number | null;
  employee_count: number | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  logo_url: string | null;
  founded_year: number | null;
  contacts: any[];
  tags: string[] | null;
  created_by: number | null;
  // Computed
  applications_count?: number;
  employees_count?: number;
  // SUDOP fields
  sudop_data?: any;
  sudop_checked_at?: string;
  sudop_de_minimis_3y_eur?: number;
  sudop_total_cases?: number;
  // SUDOP list fields (from getAll)
  kfs_cases?: number;
  last_kfs_date?: string;
  kfs_total_eur?: number;
  last_de_minimis_date?: string;
  utilization_pct?: number;
  created_at: string;
  updated_at: string;
}

export interface CompanyEmployee {
  id: number;
  company_id: number;
  application_id: number | null;
  client_id: number | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  pesel: string | null;
  position: string | null;
  department: string | null;
  role_description: string | null;
  employment_type: 'umowa_o_prace' | 'umowa_zlecenie' | 'b2b' | 'umowa_o_dzielo' | 'inne';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Collateral {
  id: number;
  application_id: number;
  type: 'weksel' | 'poreczenie' | 'hipoteka' | 'cesja_wierzytelnosci' | 'zastaw' | 'inne';
  description: string | null;
  amount: number | null;
  currency: string;
  status: 'active' | 'released' | 'expired' | 'enforced';
  valid_until: string | null;
  document_ref: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CrossSellLead {
  id: number;
  source_application_id: number;
  product_code: string;
  funding_source: 'KFS' | 'BUR' | 'EFS' | 'OTHER';
  funding_amount: number;
  company_id: number | null;
  employees_json: CompanyEmployee[];
  status: 'pending_session_assignment' | 'session_assigned' | 'enrolled' | 'cancelled';
  session_id: number | null;
  notes: string | null;
  // joined fields
  first_name?: string;
  last_name?: string;
  company_name?: string;
  company_nip?: string;
  course_name?: string;
  session_start?: string;
  created_at: string;
  updated_at: string;
}

export interface DeMinimisReport {
  companyId: number;
  companyName: string;
  companyNip: string;
  currentTotal: number;
  newTotal: number;
  limitEur: number;
  limitPln: number;
  exceedsLimit: boolean;
  remainingEur: number;
  remainingPln: number;
  utilizationPercent: number;
  warning: string | null;
}

export interface SudopAidCase {
  entity_granting_aid: string;
  entity_granting_nip: string;
  law: string;
  aid_measure_number: string | null;
  aid_granted_on: string;
  beneficiary_nip: string;
  beneficiary_name: string;
  beneficiary_type: string;
  sector_pkd: string;
  sector_name: string;
  municipality: string;
  purpose_code: string;
  purpose_name: string;
  aid_form_code: string;
  aid_form_name: string;
  nominal_value_pln: number;
  brutto_value_pln: number;
  brutto_value_eur: number;
}

export interface SudopDeMinimisReport {
  nip: string;
  companyName: string | null;
  totalCases: number;
  deMinimisSum3yEur: number;
  deMinimisSum3yPln: number;
  limitEur: number;
  remainingEur: number;
  utilizationPercent: number;
  exceedsLimit: boolean;
  warning: string | null;
  rollingWindowStart: string;
  rollingWindowEnd: string;
  deMinimisHistory: SudopAidCase[];
  allCases: SudopAidCase[];
  fetchedAt: string;
}

export interface LoanScheduleEntry {
  month: number;
  payment_date: string;
  capital: number;
  interest: number;
  installment: number;
  remaining_balance: number;
  is_grace: boolean;
}

export interface LoanScheduleResult {
  schedule: LoanScheduleEntry[];
  summary: {
    principal: number;
    total_interest: number;
    total_payments: number;
    monthly_installment?: number;
    first_installment?: number;
    last_installment?: number;
    grace_months: number;
    repayment_months: number;
    annual_rate_percent: number;
  };
}

export interface LoanScheduleInput {
  principal: number;
  annual_rate_percent: number;
  term_months: number;
  grace_months: number;
  start_date: string;
  schedule_type: 'annuity' | 'capital';
}

// ── API ──

export interface CompanyContact {
  id: string;
  name: string;
  role: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  created_at: string;
}

export interface CompanyStats {
  totals: {
    total: number;
    active: number;
    prospects: number;
    partners: number;
    suppliers: number;
    total_revenue: number;
    total_employees: number;
    avg_employees: number;
  };
  bySize: { size: string; count: number }[];
  byIndustry: { industry: string; count: number; revenue: number; employees: number }[];
  byVoivodeship: { voivodeship: string; count: number }[];
  byStatus: { status: string; count: number }[];
  recentCompanies: any[];
}

export interface DuplicateEntry {
  id1: number; name1: string; nip1: string;
  id2: number; name2: string; nip2: string;
  reason: string; score: number;
}

export const companiesApi = {
  // Companies CRUD
  getAll: (params?: { search?: string; limit?: number; offset?: number; size?: string; industry?: string; voivodeship?: string; status?: string; sort_by?: string; sort_dir?: string }) =>
    authAxios.get<{ companies: Company[]; total: number; limit: number; offset: number }>(
      '/api/companies', { params }
    ).then(r => r.data),

  getById: (id: number) =>
    authAxios.get<{ company: Company; employees: CompanyEmployee[]; applications: any[]; trainings: any[] }>(
      `/api/companies/${id}`
    ).then(r => r.data),

  create: (data: Partial<Company>) =>
    authAxios.post<Company>('/api/companies', data).then(r => r.data),

  update: (id: number, data: Partial<Company>) =>
    authAxios.patch<Company>(`/api/companies/${id}`, data).then(r => r.data),

  // Search
  search: (q: string) =>
    authAxios.get<any[]>('/api/companies/search', { params: { q } }).then(r => r.data),

  // Stats
  getStats: () =>
    authAxios.get<CompanyStats>('/api/companies/stats').then(r => r.data),

  // By industry / voivodeship
  getByIndustry: () =>
    authAxios.get<any[]>('/api/companies/by-industry').then(r => r.data),

  getByVoivodeship: () =>
    authAxios.get<any[]>('/api/companies/by-voivodeship').then(r => r.data),

  // Voivodeships with counts
  getVoivodeships: () =>
    authAxios.get<{ voivodeships: { voivodeship: string; count: number }[]; unmatched: number }>('/api/companies/voivodeships').then(r => r.data),

  // Industries list
  getIndustries: () =>
    authAxios.get<string[]>('/api/companies/industries').then(r => r.data),

  // Export CSV
  exportCsv: () =>
    authAxios.get('/api/companies/export-csv', { responseType: 'blob' }).then(r => {
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = r.headers['content-disposition']?.match(/filename="(.+)"/)?.[1] || `firmy-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }),

  // Duplicates
  getDuplicates: () =>
    authAxios.get<DuplicateEntry[]>('/api/companies/duplicates').then(r => r.data),

  // NIP lookup
  nipLookup: (nip: string) =>
    authAxios.get<any>(`/api/companies/nip-lookup/${nip}`).then(r => r.data),

  // Contacts
  getContacts: (id: number) =>
    authAxios.get<CompanyContact[]>(`/api/companies/${id}/contacts`).then(r => r.data),

  addContact: (id: number, data: { name: string; role?: string; email?: string; phone?: string; notes?: string }) =>
    authAxios.post<CompanyContact>(`/api/companies/${id}/contacts`, data).then(r => r.data),

  deleteContact: (companyId: number, contactId: string) =>
    authAxios.delete(`/api/companies/${companyId}/contacts/${contactId}`).then(r => r.data),

  // Import
  importCompanies: (companies: any[]) =>
    authAxios.post<{ imported: number; skipped: number; errors: number; total: number; results: any[] }>(
      '/api/companies/import', { companies }
    ).then(r => r.data),

  // Employees
  addEmployee: (companyId: number, data: Partial<CompanyEmployee>) =>
    authAxios.post<CompanyEmployee>(`/api/companies/${companyId}/employees`, data).then(r => r.data),

  updateEmployee: (companyId: number, empId: number, data: Partial<CompanyEmployee>) =>
    authAxios.patch<CompanyEmployee>(`/api/companies/${companyId}/employees/${empId}`, data).then(r => r.data),

  // Sub-lists
  getApplications: (id: number) =>
    authAxios.get<any[]>(`/api/companies/${id}/applications`).then(r => r.data),

  getTrainings: (id: number) =>
    authAxios.get<any[]>(`/api/companies/${id}/trainings`).then(r => r.data),

  // KFS / De Minimis
  generateKfsJustification: (id: number, params: {
    employeeName: string;
    position: string;
    roleDescription?: string;
    trainingName: string;
    trainingArea?: string;
    expectedOutcomes?: string;
  }) =>
    authAxios.post<{ justification: string }>(`/api/companies/${id}/kfs-justification`, params).then(r => r.data),

  getDeMinimisReport: (id: number) =>
    authAxios.get<DeMinimisReport>(`/api/companies/${id}/de-minimis`).then(r => r.data),

  checkDeMinimis: (id: number, amountEur: number) =>
    authAxios.post<DeMinimisReport>(`/api/companies/${id}/de-minimis/check`, { amount_eur: amountEur }).then(r => r.data),

  lookupNip: (nip: string) =>
    authAxios.get<{ source: string; company?: Company; message?: string; nip: string }>(
      '/api/companies/lookup', { params: { nip } }
    ).then(r => r.data),

  enrichCeidg: (id: number) =>
    authAxios.post<{ enriched: boolean; source?: string; updates?: Record<string, any>; message?: string }>(
      `/api/companies/${id}/enrich-ceidg`
    ).then(r => r.data),

  bulkEnrich: () =>
    authAxios.post<{ enriched: number; total: number; details: any[] }>(
      '/api/companies/bulk-enrich'
    ).then(r => r.data),

  // Loan calculator
  calculateLoanSchedule: (input: LoanScheduleInput) =>
    authAxios.post<LoanScheduleResult>('/api/companies/loan-calculator', input).then(r => r.data),

  getLoanSummary: (params: { principal: number; annual_rate_percent: number; term_months: number; grace_months?: number }) =>
    authAxios.post<{ monthly_installment_approx: number; total_cost: number; total_interest: number }>(
      '/api/companies/loan-summary', params
    ).then(r => r.data),

  // Collaterals
  getCollaterals: (appId: number) =>
    authAxios.get<Collateral[]>(`/api/companies/collaterals/by-application/${appId}`).then(r => r.data),

  addCollateral: (data: Partial<Collateral> & { application_id: number }) =>
    authAxios.post<Collateral>('/api/companies/collaterals', data).then(r => r.data),

  updateCollateral: (id: number, data: Partial<Collateral>) =>
    authAxios.patch<Collateral>(`/api/companies/collaterals/${id}`, data).then(r => r.data),

  deleteCollateral: (id: number) =>
    authAxios.delete(`/api/companies/collaterals/${id}`).then(r => r.data),

  // Cross-Sell
  getCrossSellLeads: (status?: string) =>
    authAxios.get<CrossSellLead[]>('/api/companies/cross-sell-leads', { params: { status } }).then(r => r.data),

  assignCrossSellSession: (leadId: number, sessionId: number | null, status: string) =>
    authAxios.patch<CrossSellLead>(`/api/companies/cross-sell-leads/${leadId}`, { session_id: sessionId, status }).then(r => r.data),

  // SUDOP — De Minimis verification (UOKiK)
  getSudopReport: (id: number) =>
    authAxios.get<SudopDeMinimisReport>(`/api/companies/${id}/de-minimis/sudop`).then(r => r.data),

  checkSudopByNip: (nip: string) =>
    authAxios.get<SudopDeMinimisReport>('/api/companies/sudop-check', { params: { nip } }).then(r => r.data),

  syncSudop: (id: number) =>
    authAxios.post<{ synced: boolean; oldValue: number; newValue: number; difference: number; report: SudopDeMinimisReport }>(
      `/api/companies/${id}/de-minimis/sync-sudop`
    ).then(r => r.data),

  downloadSudopReport: (id: number) =>
    authAxios.get(`/api/companies/${id}/de-minimis/report`, { responseType: 'blob' }).then(r => {
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = r.headers['content-disposition']?.match(/filename="(.+)"/)?.[1] || `de-minimis-report.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }),
};
